<template>
  <div v-if="visible" class="intro-root">
    <div class="intro-mask" :class="{ 'is-cutout': !!spotlight }" @click="skip"></div>
    <div v-if="spotlight" class="intro-spotlight" :style="spotlight"></div>
    <div class="intro-card" :style="cardStyle">
      <div class="intro-step">
        <span v-if="guideName" class="intro-mod">{{ guideName }}</span>
        第 {{ stepIndex + 1 }} / {{ steps.length }} 步
      </div>
      <h3>{{ current.title }}</h3>
      <p>{{ current.content }}</p>
      <div class="intro-actions">
        <el-button type="text" @click="skip">跳过</el-button>
        <el-button type="text" @click="dontShowAgain">本页不再提示</el-button>
        <div class="spacer"></div>
        <el-button v-if="stepIndex > 0" @click="prev">上一步</el-button>
        <el-button type="primary" @click="next">{{ isLast ? '完成' : '下一步' }}</el-button>
      </div>
    </div>
  </div>
</template>

<script>
import {
  GLOBAL_INTRO_STEPS,
  MODULE_INTRO_GUIDES,
  resolveModuleGuide,
  moduleDoneKey
} from '@/config/introGuides';

const GLOBAL_DONE_KEY = 'zywy_intro_done_v1';

export default {
  name: 'IntroTour',
  data() {
    return {
      visible: false,
      stepIndex: 0,
      steps: GLOBAL_INTRO_STEPS,
      guideName: '',
      currentModuleKey: '',
      cardStyle: { top: '80px', left: '50%', transform: 'translateX(-50%)' },
      spotlight: null,
      _routeHook: null
    };
  },
  computed: {
    current() { return this.steps[this.stepIndex] || {}; },
    isLast() { return this.stepIndex >= this.steps.length - 1; }
  },
  mounted() {
    this.$root.$on('start-intro', this.onStartEvent);
    this._alive = true;
    this.$router.afterEach((to) => {
      if (!this._alive) return;
      this.$nextTick(() => this.maybeAutoModule(to.path));
    });
    this.$nextTick(() => {
      if (!localStorage.getItem(GLOBAL_DONE_KEY)) {
        setTimeout(() => this.startGlobal(), 500);
      } else {
        setTimeout(() => this.maybeAutoModule(this.$route.path), 400);
      }
    });
    window.addEventListener('resize', this.placeCard);
  },
  beforeDestroy() {
    this._alive = false;
    this.$root.$off('start-intro', this.onStartEvent);
    window.removeEventListener('resize', this.placeCard);
  },
  methods: {
    onStartEvent(payload) {
      // payload: undefined | 'global' | 'module' | moduleKey
      if (!payload || payload === 'module') {
        const mod = resolveModuleGuide(this.$route.path);
        if (mod) {
          this.startModule(mod, true);
          return;
        }
        this.startGlobal(true);
        return;
      }
      if (payload === 'global') {
        this.startGlobal(true);
        return;
      }
      const found = Object.values(MODULE_INTRO_GUIDES).find(g => g.key === payload);
      if (found) this.startModule(found, true);
      else this.startGlobal(true);
    },
    startGlobal(force) {
      if (!force && localStorage.getItem(GLOBAL_DONE_KEY)) return;
      this.guideName = '系统引导';
      this.currentModuleKey = '';
      this.steps = GLOBAL_INTRO_STEPS;
      this.stepIndex = 0;
      this.visible = true;
      this.$nextTick(() => this.placeCard());
    },
    startModule(mod, force) {
      if (!mod) return;
      const key = moduleDoneKey(mod.key);
      if (!force && localStorage.getItem(key)) return;
      this.guideName = mod.name;
      this.currentModuleKey = mod.key;
      this.steps = mod.steps || [];
      this.stepIndex = 0;
      this.visible = true;
      this.$nextTick(() => this.placeCard());
    },
    maybeAutoModule(path) {
      if (this.visible) return;
      const mod = resolveModuleGuide(path);
      if (!mod) return;
      if (localStorage.getItem(moduleDoneKey(mod.key))) return;
      // 全局引导未完成时先不抢
      if (!localStorage.getItem(GLOBAL_DONE_KEY)) return;
      setTimeout(() => this.startModule(mod, false), 350);
    },
    findTarget(selector) {
      if (!selector) return null;
      const parts = String(selector).split(',').map(s => s.trim()).filter(Boolean);
      for (const p of parts) {
        const el = document.querySelector(p);
        if (el) return el;
      }
      return null;
    },
    placeCard() {
      if (!this.visible) return;
      const el = this.findTarget(this.current.selector);
      if (!el) {
        this.spotlight = null;
        this.cardStyle = { top: '96px', left: '50%', transform: 'translateX(-50%)' };
        return;
      }
      el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      const rect = el.getBoundingClientRect();
      const pad = 6;
      this.spotlight = {
        top: Math.max(0, rect.top - pad) + 'px',
        left: Math.max(0, rect.left - pad) + 'px',
        width: rect.width + pad * 2 + 'px',
        height: rect.height + pad * 2 + 'px'
      };
      let top = rect.bottom + 12;
      let left = Math.min(Math.max(rect.left, 16), window.innerWidth - 360);
      if (top + 210 > window.innerHeight) {
        top = Math.max(16, rect.top - 200);
      }
      this.cardStyle = { top: top + 'px', left: left + 'px', transform: 'none' };
    },
    next() {
      if (this.isLast) {
        this.finish(true);
        return;
      }
      this.stepIndex += 1;
      this.$nextTick(() => this.placeCard());
    },
    prev() {
      if (this.stepIndex > 0) {
        this.stepIndex -= 1;
        this.$nextTick(() => this.placeCard());
      }
    },
    skip() { this.finish(false); },
    dontShowAgain() { this.finish(true); },
    finish(markDone) {
      if (markDone) {
        if (this.currentModuleKey) {
          localStorage.setItem(moduleDoneKey(this.currentModuleKey), '1');
        } else {
          localStorage.setItem(GLOBAL_DONE_KEY, '1');
        }
      } else if (!this.currentModuleKey) {
        // 跳过全局也记完成，避免反复打断；模块跳过不记，下次还可进
        localStorage.setItem(GLOBAL_DONE_KEY, '1');
      }
      this.visible = false;
      this.spotlight = null;
    }
  }
};
</script>

<style lang="scss" scoped>
.intro-root { position: fixed; inset: 0; z-index: 4000; pointer-events: none; }
.intro-mask {
  position: absolute; inset: 0;
  background: rgba(0, 0, 0, .48);
  pointer-events: auto;
  z-index: 0;
}
.intro-mask.is-cutout {
  background: transparent;
}
.intro-spotlight {
  position: absolute;
  border-radius: 6px;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, .48);
  background: transparent;
  border: 2px solid #22bf95;
  pointer-events: none;
  z-index: 1;
  transition: all .2s ease;
}
.intro-card {
  position: absolute;
  width: 360px;
  background: #fff;
  border-radius: 8px;
  padding: 16px 18px;
  box-shadow: 0 8px 28px rgba(0, 0, 0, .2);
  z-index: 2;
  pointer-events: auto;
}
.intro-step { font-size: 12px; color: #909399; margin-bottom: 6px; }
.intro-mod {
  display: inline-block;
  margin-right: 8px;
  padding: 0 6px;
  border-radius: 3px;
  background: #e8f8f2;
  color: #109f7c;
  font-weight: 600;
}
.intro-card h3 { margin: 0 0 8px; font-size: 16px; color: #303133; }
.intro-card p { margin: 0; font-size: 13px; color: #606266; line-height: 1.65; }
.intro-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 14px;
}
.spacer { flex: 1; min-width: 8px; }
</style>
